import { ArrowUp, ArrowDown, Trash2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AiSuggest } from "@/components/docs/AiSuggest";

const uid = () => Math.random().toString(36).slice(2, 10);

const F = ({ label, value, onChange, testId, placeholder, className = "" }) => (
  <div className={className}>
    <Label className="text-xs font-semibold text-ink-soft">{label}</Label>
    <Input className="field mt-1.5" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </div>
);

const Section = ({ value, title, count, children }) => (
  <AccordionItem value={value} className="mb-3 rounded-xl border border-paper-border bg-white px-5">
    <AccordionTrigger className="py-4 hover:no-underline" data-testid={`section-${value}-toggle`}>
      <span className="flex items-baseline gap-3 font-serif text-lg text-ink">{title}{count != null && <span className="font-mono text-[11px] text-taupe">{String(count).padStart(2, "0")}</span>}</span>
    </AccordionTrigger>
    <AccordionContent className="pb-5">{children}</AccordionContent>
  </AccordionItem>
);

const ItemControls = ({ i, n, onMove, onRemove, prefix }) => (
  <div className="flex gap-1">
    <button type="button" disabled={i === 0} onClick={() => onMove(i, -1)} className="rounded p-1.5 text-ink-soft hover:bg-paper-subtle disabled:opacity-30" data-testid={`${prefix}-${i}-up`} aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
    <button type="button" disabled={i === n - 1} onClick={() => onMove(i, 1)} className="rounded p-1.5 text-ink-soft hover:bg-paper-subtle disabled:opacity-30" data-testid={`${prefix}-${i}-down`} aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
    <button type="button" onClick={() => onRemove(i)} className="rounded p-1.5 text-ink-soft hover:bg-red-50 hover:text-red-600" data-testid={`${prefix}-${i}-remove`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
  </div>
);

function useList(data, onChange, key) {
  const list = data[key] || [];
  const set = (l) => onChange({ ...data, [key]: l });
  return {
    list,
    edit: (i, patch) => set(list.map((x, j) => (j === i ? { ...x, ...patch } : x))),
    add: (item) => set([...list, { id: uid(), ...item }]),
    remove: (i) => set(list.filter((_, j) => j !== i)),
    move: (i, dir) => { const l = [...list]; [l[i], l[i + dir]] = [l[i + dir], l[i]]; set(l); },
  };
}

export const ResumeForm = ({ data, onChange, api }) => {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  const exp = useList(data, onChange, "experience");
  const edu = useList(data, onChange, "education");
  const improve = (kind, text, context) => (onText) => api.stream("/ai/improve", { kind, text, context }, onText);

  return (
    <Accordion type="multiple" defaultValue={["personal", "summary", "experience"]} data-testid="resume-form">
      <Section value="personal" title="Personal details">
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Full name" value={data.name} onChange={set("name")} testId="input-name" placeholder="Jordan Lee" />
          <F label="Job title" value={data.title} onChange={set("title")} testId="input-title" placeholder="Marketing Manager" />
          <F label="Email" value={data.email} onChange={set("email")} testId="input-email" />
          <F label="Phone" value={data.phone} onChange={set("phone")} testId="input-phone" />
          <F label="Location" value={data.location} onChange={set("location")} testId="input-location" placeholder="City, State" />
          <F label="Website or LinkedIn" value={data.website} onChange={set("website")} testId="input-website" />
        </div>
      </Section>

      <Section value="summary" title="Professional summary">
        <Textarea className="field min-h-[110px]" value={data.summary || ""} onChange={(e) => set("summary")(e.target.value)} data-testid="input-summary"
          placeholder="Two or three sentences about who you are and what you do best." />
        <div className="mt-3">
          <AiSuggest testId="ai-improve-summary" disabled={!data.summary?.trim()} run={improve("summary", data.summary, data.title)} onAccept={set("summary")} />
        </div>
      </Section>

      <Section value="experience" title="Work experience" count={exp.list.length}>
        <div className="space-y-4">
          {exp.list.map((e, i) => (
            <div key={e.id} className="rounded-lg border border-paper-border bg-paper p-4" data-testid={`experience-${i}`}>
              <div className="mb-3 flex items-center justify-between"><span className="font-mono text-[11px] text-taupe">POSITION {i + 1}</span>
                <ItemControls i={i} n={exp.list.length} onMove={exp.move} onRemove={exp.remove} prefix="experience" /></div>
              <div className="grid gap-3 sm:grid-cols-2">
                <F label="Role" value={e.role} onChange={(v) => exp.edit(i, { role: v })} testId={`experience-${i}-role`} />
                <F label="Company" value={e.company} onChange={(v) => exp.edit(i, { company: v })} testId={`experience-${i}-company`} />
                <F label="Location" value={e.location} onChange={(v) => exp.edit(i, { location: v })} testId={`experience-${i}-location`} />
                <div className="grid grid-cols-2 gap-2">
                  <F label="Start" value={e.start} onChange={(v) => exp.edit(i, { start: v })} testId={`experience-${i}-start`} placeholder="2021" />
                  <F label="End" value={e.end} onChange={(v) => exp.edit(i, { end: v })} testId={`experience-${i}-end`} placeholder="Present" />
                </div>
              </div>
              <Label className="mt-3 block text-xs font-semibold text-ink-soft">Achievements (one per line)</Label>
              <Textarea className="field mt-1.5 min-h-[100px]" value={e.bullets || ""} onChange={(ev) => exp.edit(i, { bullets: ev.target.value })} data-testid={`experience-${i}-bullets`} />
              <div className="mt-3">
                <AiSuggest testId={`ai-improve-bullets-${i}`} label="Improve bullets" disabled={!e.bullets?.trim()}
                  run={improve("bullets", e.bullets, `${e.role} at ${e.company}`)} onAccept={(v) => exp.edit(i, { bullets: v })} />
              </div>
            </div>
          ))}
          <button type="button" className="btn-ink w-full" onClick={() => exp.add({ role: "", company: "", location: "", start: "", end: "", bullets: "" })} data-testid="add-experience-btn">
            <Plus className="h-4 w-4" /> Add position
          </button>
        </div>
      </Section>

      <Section value="education" title="Education" count={edu.list.length}>
        <div className="space-y-4">
          {edu.list.map((e, i) => (
            <div key={e.id} className="rounded-lg border border-paper-border bg-paper p-4" data-testid={`education-${i}`}>
              <div className="mb-3 flex items-center justify-between"><span className="font-mono text-[11px] text-taupe">ENTRY {i + 1}</span>
                <ItemControls i={i} n={edu.list.length} onMove={edu.move} onRemove={edu.remove} prefix="education" /></div>
              <div className="grid gap-3 sm:grid-cols-2">
                <F label="Degree" value={e.degree} onChange={(v) => edu.edit(i, { degree: v })} testId={`education-${i}-degree`} />
                <F label="School" value={e.school} onChange={(v) => edu.edit(i, { school: v })} testId={`education-${i}-school`} />
                <F label="Start" value={e.start} onChange={(v) => edu.edit(i, { start: v })} testId={`education-${i}-start`} />
                <F label="End" value={e.end} onChange={(v) => edu.edit(i, { end: v })} testId={`education-${i}-end`} />
                <F className="sm:col-span-2" label="Details (optional)" value={e.details} onChange={(v) => edu.edit(i, { details: v })} testId={`education-${i}-details`} />
              </div>
            </div>
          ))}
          <button type="button" className="btn-ink w-full" onClick={() => edu.add({ degree: "", school: "", start: "", end: "", details: "" })} data-testid="add-education-btn">
            <Plus className="h-4 w-4" /> Add education
          </button>
        </div>
      </Section>

      <Section value="skills" title="Skills">
        <Label className="text-xs font-semibold text-ink-soft">Separate skills with commas</Label>
        <Textarea className="field mt-1.5 min-h-[80px]" value={data.skills || ""} onChange={(e) => set("skills")(e.target.value)} data-testid="input-skills" />
      </Section>
    </Accordion>
  );
};
